import { useMemo } from 'react';
import {
  Slider as AriaSlider,
  SliderThumb,
  SliderTrack,
  Label,
} from 'react-aria-components';
import './CustomSlider.scss';

export interface CustomSliderPropsDefault {
  type: 'default';
  value: number;
  onChange: (value: number) => void;
}

export interface CustomSliderPropsRange {
  type: 'range';
  value: [number, number];
  onChange: (value: [number, number]) => void;
}

export type CustomSliderProps = (
  | CustomSliderPropsDefault
  | CustomSliderPropsRange
) & {
  ariaLabel?: string;
  vertical?: boolean;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  reverse?: boolean;
  error?: boolean;
  track?: boolean;
  additionalClass?: string;
};

export default function CustomSlider({
  type,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  vertical = false,
  disabled = false,
  reverse = false,
  error = false,
  track = true,
  ariaLabel,
  additionalClass,
}: CustomSliderProps) {
  const isRange = useMemo(() => type === 'range', [type]);

  return (
    <AriaSlider
      className={`custom-slider ${vertical ? 'vertical' : 'horizontal'} ${additionalClass ?? ''}`}
      minValue={min}
      maxValue={max}
      step={step}
      value={value}
      onChange={(v) => {
        if (type === 'range') {
          onChange(v as [number, number]);
        } else {
          onChange(v as number);
        }
      }}
      orientation={vertical ? 'vertical' : 'horizontal'}
      isDisabled={disabled}
      aria-label={ariaLabel}
    >
      <Label className="slider-label">{ariaLabel}</Label>
      <div className="slider-wrapper">
        {track && (
          <div
            className={`progress-bar ${vertical ? 'vertical' : 'horizontal'}`}
            style={{
              [vertical ? 'height' : 'width']: isRange
                ? `${
                    (((value as [number, number])[1] - (value as [number, number])[0]) /
                      (max - min)) *
                    100
                  }%`
                : `${(((value as number) - min) / (max - min)) * 100}%`,
              [vertical ? 'top' : 'left']: reverse
                ? isRange
                  ? `${
                      ((max - (value as [number, number])[1]) / (max - min)) *
                      100
                    }%`
                  : `${((max - (value as number)) / (max - min)) * 100}%`
                : isRange
                  ? `${(((value as [number, number])[0] - min) / (max - min)) * 100}%`
                  : '0%',
            }}
          />
        )}
        <SliderTrack className={`track ${vertical ? 'vertical' : 'horizontal'}`}>
          {({ state }) =>
            state.values.map((_, i) => (
              <SliderThumb
                key={i}
                index={i}
                className={`slider-thumb ${error ? 'error' : ''} ${vertical ? 'vertical' : 'horizontal'}`}
              />
            ))
          }
        </SliderTrack>
      </div>
    </AriaSlider>
  );
}
